/* Pop-up di primo accesso: persistenza robusta, focus trap, Escape, blocco scroll, anteprima editor */
if (!customElements.get('welcome-popup')) {
  const memoryStore = {};

  const storage = {
    get(key) {
      try {
        const v = window.localStorage.getItem(key);
        if (v !== null) return v;
      } catch {}
      try {
        const match = document.cookie.match(new RegExp('(?:^|; )' + key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '=([^;]*)'));
        if (match) return decodeURIComponent(match[1]);
      } catch {}
      return Object.prototype.hasOwnProperty.call(memoryStore, key) ? memoryStore[key] : null;
    },
    set(key, value, days) {
      memoryStore[key] = value;
      try {
        window.localStorage.setItem(key, value);
      } catch {}
      try {
        const expires = new Date(Date.now() + days * 864e5).toUTCString();
        document.cookie = key + '=' + encodeURIComponent(value) + '; expires=' + expires + '; path=/; SameSite=Lax';
      } catch {}
    },
  };

  class WelcomePopup extends HTMLElement {
    constructor() {
      super();
      this.onKeydown = this.onKeydown.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onSectionSelect = this.onSectionSelect.bind(this);
      this.onSectionDeselect = this.onSectionDeselect.bind(this);
    }

    connectedCallback() {
      this.version = this.dataset.version || '2';
      this.days = parseInt(this.dataset.days, 10) || 30;
      this.delay = parseInt(this.dataset.delay, 10) || 0;
      this.key = 'am_welcome_popup_' + this.version.replace(/[^a-zA-Z0-9_-]/g, '');
      this.designMode = !!(window.Shopify && window.Shopify.designMode);
      this.previewInEditor = this.dataset.editorPreview === 'true';
      this.dialog = this.querySelector('[role="dialog"]');
      this.isOpen = false;

      this.addEventListener('click', this.onClick);

      if (this.designMode) {
        document.addEventListener('shopify:section:select', this.onSectionSelect);
        document.addEventListener('shopify:section:deselect', this.onSectionDeselect);
        if (this.previewInEditor) this.open();
        return;
      }

      if (this.dataset.enabled !== 'true') return;
      if (this.alreadyDismissed()) return;
      this.timer = window.setTimeout(() => this.open(), this.delay);
    }

    disconnectedCallback() {
      window.clearTimeout(this.timer);
      this.removeEventListener('click', this.onClick);
      document.removeEventListener('shopify:section:select', this.onSectionSelect);
      document.removeEventListener('shopify:section:deselect', this.onSectionDeselect);
      if (this.isOpen) this.close(false);
    }

    alreadyDismissed() {
      const raw = storage.get(this.key);
      if (!raw) return false;
      const ts = parseInt(raw, 10);
      if (!ts) return false;
      return Date.now() - ts < this.days * 864e5;
    }

    onSectionSelect(event) {
      if (event.target && event.target.contains(this)) this.open();
    }

    onSectionDeselect(event) {
      if (event.target && event.target.contains(this)) this.close(false);
    }

    open() {
      if (this.isOpen) return;
      this.isOpen = true;
      this.previousFocus = document.activeElement;
      this.hidden = false;
      document.body.classList.add('am-popup-open');
      document.addEventListener('keydown', this.onKeydown);
      window.requestAnimationFrame(() => {
        this.dialog.focus({ preventScroll: true });
      });
    }

    close(persist = true) {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.hidden = true;
      document.body.classList.remove('am-popup-open');
      document.removeEventListener('keydown', this.onKeydown);
      if (persist && !this.designMode) storage.set(this.key, String(Date.now()), this.days);
      if (this.previousFocus && typeof this.previousFocus.focus === 'function') {
        this.previousFocus.focus({ preventScroll: true });
      }
    }

    onClick(event) {
      const closeTrigger = event.target.closest('[data-popup-close]');
      if (closeTrigger) {
        event.preventDefault();
        this.close(true);
        return;
      }
      if (event.target.classList.contains('am-popup')) {
        this.close(true);
      }
    }

    focusableElements() {
      return Array.from(
        this.dialog.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])')
      ).filter((el) => el.offsetParent !== null);
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close(true);
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = this.focusableElements();
      if (focusable.length === 0) {
        event.preventDefault();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === this.dialog)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  customElements.define('welcome-popup', WelcomePopup);
}
